import type { ColumnDef, Row, SortingState } from "@tanstack/react-table";

import { memo, useCallback, useMemo, useState } from "react";

import { BulkOperationsToolbar } from "@/components/bulk-operations-toolbar";
import { Checkbox } from "@/components/checkbox";
import { DraggableTable as Table } from "@/components/table/draggable-table";
import { MOBILE_COLUMN_WIDTHS } from "@/features/requests/config/constants/columnWidths";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTableSorting } from "@/hooks/useTableSorting";

import type { ColumnConfig, ConfigurableTableProps } from "../../types/tables";

function createColumnDef<T = Record<string, unknown>>(
  columnConfig: ColumnConfig<T>,
  customProps?: Record<string, unknown>,
  isMobile = false,
): ColumnDef<T> {
  return {
    accessorKey: columnConfig.accessor as string,
    cell: ({ getValue, row }) => {
      const value = getValue();

      // Use custom cell renderer if provided, otherwise display the value
      if (columnConfig.cellRenderer) {
        const propsWithIndex = {
          ...customProps,
          rowIndex: row.index,
        };

        return columnConfig.cellRenderer(value, row.original, propsWithIndex);
      }

      // Default rendering for different data types
      if (value === null || value === undefined) {
        return <span className="text-text-tertiary">—</span>;
      }

      if (typeof value === "string" || typeof value === "number") {
        return <span className="text-sm">{value}</span>;
      }

      if (typeof value === "object" && value && "name" in value) {
        return (
          <span className="text-sm">{(value as { name: string }).name}</span>
        );
      }

      return <span className="text-sm">{String(value)}</span>;
    },
    header: columnConfig.header,
    id: columnConfig.key,
    size: isMobile
      ? (columnConfig.mobileWidth ??
        MOBILE_COLUMN_WIDTHS[columnConfig.key] ??
        columnConfig.width)
      : columnConfig.width,
  };
}

function ConfigurableTableComponent<T = Record<string, unknown>>({
  className,
  config,
  count,
  customProps,
  data,
  dataSection,
  dragDropHandlers,
  isCountLoading = false,
  isLoading = false,
  onSort,
  paginationProps,
  selectionConfig,
  setSortDescriptor,
  sortDescriptor,
  titleActions,
  useAdvancedSorting = false,
}: ConfigurableTableProps<T>) {
  const isMobile = useIsMobile();

  // Smart loading logic: Skip loading animation if count is known to be 0
  const shouldShowLoading = useMemo(() => {
    // If we don't have count info yet, show loading as normal
    if (count === undefined || isCountLoading) {
      return isLoading;
    }

    // If count is 0, skip loading and show empty state immediately
    if (count === 0) {
      return false;
    }

    // Otherwise, show loading normally
    return isLoading;
  }, [count, isCountLoading, isLoading]);
  // Basic sorting state for legacy behavior
  const [basicSorting, setBasicSorting] = useState<SortingState>(
    config.defaultSort
      ? [
          {
            desc: config.defaultSort.direction === "desc",
            id: config.defaultSort.column,
          },
        ]
      : [],
  );

  // Advanced sorting
  const { handleSort, sorting: advancedSorting } = useTableSorting({
    defaultColumn: config.defaultSort?.column || "createdAt",
    defaultDirection:
      config.defaultSort?.direction === "desc" ? "descending" : "ascending",
    setSortDescriptor: setSortDescriptor || (() => {}),
    sortDescriptor: sortDescriptor || {
      column: config.defaultSort?.column || "createdAt",
      direction:
        config.defaultSort?.direction === "desc" ? "descending" : "ascending",
    },
  });

  // Use appropriate sorting state and handler
  const sorting = useAdvancedSorting ? advancedSorting : basicSorting;
  const setSorting = useAdvancedSorting ? () => {} : setBasicSorting;

  // Memoize simple, stable row ID function
  const getRowId = useCallback(
    (row: T) => {
      if (typeof row === "object" && row !== null && "id" in row) {
        const requestId = String((row as Record<string, unknown>).id);

        // Simple, stable ID: tableId-requestId (e.g., requests-123)
        return `${config.id}-${requestId}`;
      }

      const rowIndex = data.indexOf(row);

      return `${config.id}-idx${rowIndex}`;
    },
    [data, config.id],
  );

  // Generate column definitions from config
  const columns = useMemo<ColumnDef<T>[]>(() => {
    const baseColumns = config.columns.map((columnConfig) =>
      createColumnDef(columnConfig, customProps, isMobile),
    );

    // Add selection column if selection is enabled
    if (selectionConfig?.enabled) {
      const selectionColumn: ColumnDef<T> = {
        cell: ({ row }) => {
          const id = getRowId(row.original);
          const isSelected = selectionConfig.selectedItems.has(id);

          return (
            <Checkbox
              isSelected={isSelected}
              onChange={() => {
                const newSelection = new Set(selectionConfig.selectedItems);

                if (isSelected) {
                  newSelection.delete(id);
                } else {
                  newSelection.add(id);
                }
                selectionConfig.onSelectionChange(newSelection);
              }}
            />
          );
        },
        header: () => {
          const allSelected = data.every((row) => {
            const id = getRowId(row);

            return selectionConfig.selectedItems.has(id);
          });
          const someSelected = data.some((row) => {
            const id = getRowId(row);

            return selectionConfig.selectedItems.has(id);
          });

          return (
            <Checkbox
              isIndeterminate={someSelected && !allSelected}
              isSelected={allSelected}
              onChange={() => {
                if (allSelected) {
                  selectionConfig.onClearSelection?.();
                } else {
                  selectionConfig.onSelectAll?.();
                }
              }}
            />
          );
        },
        id: "select",
        size: 50,
      };

      return [selectionColumn, ...baseColumns];
    }

    return baseColumns;
  }, [config.columns, isMobile, customProps, selectionConfig, data, getRowId]);

  // Generate sortable columns array
  const sortableColumns = useMemo(
    () =>
      config.columns
        .filter((col) => col.sortable !== false)
        .map((col) => col.key),
    [config.columns],
  );

  // Memoize sorting handler for performance
  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      if (useAdvancedSorting) {
        // Use advanced sorting handler
        handleSort(newSorting);
      } else {
        // Use basic sorting behavior
        setSorting(newSorting);

        // Notify parent of sort changes
        if (onSort && newSorting.length > 0) {
          const sortInfo = newSorting[0];

          if (sortInfo) {
            onSort(sortInfo.id, sortInfo.desc ? "desc" : "asc");
          }
        }
      }
    },
    [useAdvancedSorting, handleSort, setSorting, onSort],
  );

  // Adapt getRowClassName from TableConfig (takes T) to DraggableTable's signature (takes Row<T>)
  const getRowClassName = useMemo(() => {
    if (!config.getRowClassName) return;
    const fn = config.getRowClassName;

    return (row: Row<T>) => fn(row.original);
  }, [config.getRowClassName]);

  // Memoize pagination configuration for performance
  const paginationConfig = useMemo(() => {
    if (config.enablePagination === false) return;

    return (
      paginationProps || {
        metadata: {
          currentPage: 1,
          itemsPerPage: 25,
          totalItems: data.length,
          totalPages: Math.ceil(data.length / 25),
        },
        onPageChange: () => {}, // Fallback - all data loaded client-side
        pageSizeOptions: [10, 25, 50, 100],
        showPageSizeSelector: true,
      }
    );
  }, [config.enablePagination, paginationProps, data.length]);

  return (
    <div className={className || ""} data-section={dataSection ?? ""}>
      {/* Table Title */}
      {(config.title || config.titleNode) && (
        <div className="flex flex-col gap-2 pt-4 ml-6 pr-6 mb-4 lg:flex-row lg:items-center lg:justify-between">
          {config.titleNode || (
            <h3 className="text-lg font-semibold text-text-tertiary">
              {config.title}
            </h3>
          )}
          {titleActions && (
            <div className="flex justify-start lg:justify-end">
              {titleActions}
            </div>
          )}
        </div>
      )}

      {/* Bulk Operations Toolbar - Always show when bulk operations are available */}
      {selectionConfig?.bulkOperations && (
        <BulkOperationsToolbar
          isLoading={selectionConfig.isLoading}
          operations={selectionConfig.bulkOperations}
          selectedCount={selectionConfig.selectedItems.size}
        />
      )}

      {/* Table */}
      <div className="w-full flex-1">
        <Table
          columns={columns}
          data={data}
          enableDragAndDrop={config.enableDragAndDrop || false}
          getRowClassName={getRowClassName}
          getRowId={getRowId}
          isLoading={shouldShowLoading}
          isServerSideSorting={useAdvancedSorting || false}
          mobileColumnWidths={MOBILE_COLUMN_WIDTHS}
          onReorder={dragDropHandlers?.onReorder}
          onSave={dragDropHandlers?.onSave}
          onSort={handleSortingChange}
          pagination={paginationConfig}
          sortableColumns={
            config.enableSorting === false ? [] : sortableColumns
          }
          sorting={sorting}
        />
      </div>
    </div>
  );
}

export const ConfigurableTable = memo(ConfigurableTableComponent) as <
  T = Record<string, unknown>,
>(
  props: ConfigurableTableProps<T>,
) => JSX.Element;
